import {
    AssetTableRowData,
    TokenRowData,
} from "~/modules/assets/components/AssetTable/types";
import { Asset } from "~/modules/assets/types";
import { Manager } from "~/modules/managers";
import { Network } from "~/modules/networks/types";
import { Token } from "~/modules/tokens/types";

export enum MeasureNames {
    yield_to_maturity_percent = "yield_to_maturity_percent",
    cash_drag_percent = "cash_drag_percent",
    defaulted_loans_amount_percent = "defaulted_loans_amount_percent",
    net_asset_value_dollar = "net_asset_value_dollar",
    price_dollar = "price_dollar",
    m2_money_stock_dollar = "m2_money_stock_dollar",
    daily_transfer_volume_dollar = "daily_transfer_volume_dollar",
    cumulative_repaid_principal_dollar = "cumulative_repaid_principal_dollar",
    funded_assets_dollar = "funded_assets_dollar",
    available_capacity_dollar = "available_capacity_dollar",
    interest_paid_dollar = "interest_paid_dollar",
    idle_capital_dollar = "idle_capital_dollar",
    outstanding_principal_dollar = "outstanding_principal_dollar",
    cumulative_issued_amount_dollar = "cumulative_issued_amount_dollar",
    written_off_principal_dollar = "written_off_principal_dollar",
    outstanding_capital_dollar = "outstanding_capital_dollar",
    principal_paid_dollar = "principal_paid_dollar",
    circulating_asset_value_dollar = "circulating_asset_value_dollar",
    total_asset_value_dollar = "total_asset_value_dollar",
    drawndown_assets_dollar = "drawndown_assets_dollar",
    cumulative_interest_earned_dollar = "cumulative_interest_earned_dollar",
    adjusted_daily_mints_dollar = "adjusted_daily_mints_dollar",
    adjusted_daily_burns_dollar = "adjusted_daily_burns_dollar",
    daily_mints_dollar = "daily_mints_dollar",
    daily_burns_dollar = "daily_burns_dollar",
    recovered_assets_dollar = "recovered_assets_dollar",
    maximum_capacity_dollar = "maximum_capacity_dollar",
    market_value_dollar = "market_value_dollar",
    circulating_market_value_dollar = "circulating_market_value_dollar",
    cumulative_interest_earned_token = "cumulative_interest_earned_token",
    cumulative_issued_amount_token = "cumulative_issued_amount_token",
    available_capacity_token = "available_capacity_token",
    adjusted_daily_burns_token = "adjusted_daily_burns_token",
    daily_burns_token = "daily_burns_token",
    drawndown_assets_token = "drawndown_assets_token",
    written_off_principal_token = "written_off_principal_token",
    total_supply_token = "total_supply_token",
    net_asset_value_token = "net_asset_value_token",
    defaulted_loans_amount_token = "defaulted_loans_amount_token",
    defaulted_loans_amount_dollar = "defaulted_loans_amount_dollar",
    funded_assets_token = "funded_assets_token",
    recovered_assets_token = "recovered_assets_token",
    principal_paid_token = "principal_paid_token",
    daily_transfer_volume_token = "daily_transfer_volume_token",
    adjusted_daily_mints_token = "adjusted_daily_mints_token",
    daily_mints_token = "daily_mints_token",
    cumulative_repaid_principal_token = "cumulative_repaid_principal_token",
    outstanding_principal_token = "outstanding_principal_token",
    circulating_supply_token = "circulating_supply_token",
    total_asset_value_token = "total_asset_value_token",
    interest_paid_token = "interest_paid_token",
    idle_capital_token = "idle_capital_token",
    adjusted_holding_addresses_count = "adjusted_holding_addresses_count",
    trailing_7_day_active_addresses_count = "trailing_7_day_active_addresses_count",
    annually_active_addresses_count = "annually_active_addresses_count",
    trailing_30_day_active_addresses_count = "trailing_30_day_active_addresses_count",
    daily_active_addresses_count = "daily_active_addresses_count",
    trailing_365_day_active_addresses_count = "trailing_365_day_active_addresses_count",
    weekly_active_addresses_count = "weekly_active_addresses_count",
    trailing_90_day_active_addresses_count = "trailing_90_day_active_addresses_count",
    quarterly_active_addresses_count = "quarterly_active_addresses_count",
    holding_addresses_count = "holding_addresses_count",
    number_of_daily_transactions_count = "number_of_daily_transactions_count",
    monthly_active_addresses_count = "monthly_active_addresses_count",
    weekly_transfer_count = "weekly_transfer_count",
    monthly_transfer_count = "monthly_transfer_count",
    trailing_7_day_transfer_count = "trailing_7_day_transfer_count",
    trailing_30_day_transfer_count = "trailing_30_day_transfer_count",
    weekly_transfer_volume = "weekly_transfer_volume",
    monthly_transfer_volume = "monthly_transfer_volume",
    trailing_7_day_transfer_volume = "trailing_7_day_transfer_volume",
    trailing_30_day_transfer_volume = "trailing_30_day_transfer_volume",
    weighted_average_maturity_years = "weighted_average_maturity_years",
    number_of_defaulted_loans = "number_of_defaulted_loans",
    number_of_active_loans = "number_of_active_loans",
    number_of_loans_funded = "number_of_loans_funded",
    weighted_average_maturity = "weighted_average_maturity",
    yield_to_maturity = "yield_to_maturity",
    bridged_token_market_cap_dollar = "bridged_token_market_cap_dollar",
    bridged_token_supply_token = "bridged_token_supply_token",
    bridged_token_value_dollar = "bridged_token_value_dollar",
    monthly_bridge_inflow_volume = "monthly_bridge_inflow_volume",
    monthly_bridge_volume = "monthly_bridge_volume",
    trailing_30_day_bridge_volume = "trailing_30_day_bridge_volume",
    apy_7_day = "apy_7_day",
    apy_30_day = "apy_30_day",
    top_holder_concentration = "top_holder_concentration",
    top_5_holder_concentration = "top_5_holder_concentration",
    top_10_holder_concentration = "top_10_holder_concentration",
    hypothetical_10_000_performance = "hypothetical_10_000_performance",
    daily_yield_distributed_dollar = "daily_yield_distributed_dollar",
    daily_yield_rate = "daily_yield_rate",
}

type MeasureName = `${MeasureNames}`;

interface MeasureValue {
    val: number;
    val_7d: null | number;
    val_30d: null | number;
    val_90d: null | number;
    chg_7d_amt: null | number;
    chg_7d_pct: null | number;
    chg_30d_amt: null | number;
    chg_30d_pct: null | number;
    chg_90d_amt: null | number;
    chg_90d_pct: null | number;
}

export function getMeasureValue(
    asset: Asset | AssetTableRowData | TokenRowData | Token | Network | Manager,
    measure: MeasureName,
    fallbackValue?: number,
): null | MeasureValue {
    const key = measure as keyof (
        AssetTableRowData | TokenRowData | Asset | Network | Manager
    );
    const mval = asset[key] as MeasureValue | null;

    // If value isn't available...
    if (mval === null || mval === undefined) {
        // Return null if there's no fallback value provided
        if (typeof fallbackValue !== "number") {
            return null;
        }

        // Return MeasureValue populated with falback value
        return {
            val: fallbackValue,
            val_7d: null,
            val_30d: null,
            val_90d: null,
            chg_7d_amt: null,
            chg_30d_amt: null,
            chg_90d_amt: null,
            chg_7d_pct: null,
            chg_30d_pct: null,
            chg_90d_pct: null,
        };
    }

    return {
        val: mval.val,
        val_7d: mval.val_7d,
        val_30d: mval.val_30d,
        val_90d: mval.val_90d,
        chg_7d_amt: mval.chg_7d_amt,
        chg_30d_amt: mval.chg_30d_amt,
        chg_90d_amt: mval.chg_90d_amt,
        // Format percent values as decimals, for now
        chg_7d_pct: mval.chg_7d_pct ? mval.chg_7d_pct / 100 : null,
        chg_30d_pct: mval.chg_30d_pct ? mval.chg_30d_pct / 100 : null,
        chg_90d_pct: mval.chg_90d_pct ? mval.chg_90d_pct / 100 : null,
    };
}
